<!-- Splash screen - always in DOM, CSS controls visibility during print -->
<app-splash-screen
  [class.show-in-print]="showCertificationSplash || (isExternal && lockedPrint)"
  class="certification-splash"></app-splash-screen>

<!-- Main content - hidden from print when showing certification splash -->
<div class="rcc-v4__content" [ngClass]="{'exclude-from-print': showCertificationSplash || (isExternal && lockedPrint)}">

@if (showServiceInterruptionBanner) {
  <service-interruption-banner-v4 [message]="bannerContent?.serviceinterruptiondesc">
    <strong>{{ bannerContent?.serviceinterruption }}</strong>
  </service-interruption-banner-v4>
}

<div class="rcc-v4__print-header">
  <div class="rcc-v4__print-title">{{ clientReadyReportContent?.prepareClientReadyReport }}</div>
  <div class="rcc-v4__print-prepared-for" *ngIf="printClientName">
    <span>{{ printClientName }}</span>
  </div>
</div>

<!-- V4 Components Only -->
<hero-v4></hero-v4>

<!-- Introduction Modal -->
<introduction-modal></introduction-modal>

<!-- Navigation Bar -->
<nav class="rcc-nav" aria-label="In-page navigation">
  <div class="rcc-nav__inner">
    <ul class="rcc-nav__list">
      <!--
        Using Angular's newer built-in control flow syntax here with @for
        Note: track item is used to help Angular track which items have changed in the list for better performance
        $index is a built-in variable that gives the current index of the loop, which we can use to determine which
        nav item is active
       -->
      @for (item of navBarData; track item) {
        <li class="rcc-nav__item">
          <button
            type="button"
            class="rcc-nav__link"
            [class.rcc-nav__link--active]="$index === activeNavIndex"
            (click)="onNavClick($index, item.hash)"
          >
            {{ item.label }}
          </button>
        </li>
      }
    </ul>
  </div>
</nav>

<how-it-works-v4 id="overview"
                 (useCalculator)="scrollToCalculatorSection()"
                 (trySampleClient)="scrollToSampleClientsSection()"
                 (viewTermsAndDefinitions)="openTerminologySheet()"
                 (openBreakEvenTaxRate)="openSheetToBETR()"></how-it-works-v4>
<sample-clients id="sample-clients" (clientSelected)="onClientSelected()"></sample-clients>
<div id="calculator">
  <div class="rcc-v4__mobile-edit-button">
    <p class="rcc-v4__mobile-edit-text">{{ mobileInputsSheetContent?.updateTheseResults }}</p>
    <button type="button"
            class="rcc-v4__mobile-edit-btn"
            (click)="openMobileInputsSheet()">
      {{ mobileInputsSheetContent?.editButton }}
    </button>
  </div>

  <input-tabs [tabs]="inputTabsData" #inputTabsComponent id="inputTabsComponent"
  (tabChanged)="scrollToCalculatorSection()">
    <div
      *ngIf="showAppliedSampleClientBanner"
      slot="left-header"
      class="rcc-v4__sample-client-banner"
      aria-live="polite"
    >
      <img
        src="assets/svgs/check.svg"
        alt=""
        aria-hidden="true"
        class="rcc-v4__sample-client-banner-icon"
      />
      <span class="rcc-v4__sample-client-banner-text text-body-6">
        {{ sampleClientsContent?.sampleClientModals?.inputbannerprefix }} {{ appliedSampleClientTitle }}
      </span>
    </div>

    <!-- Tab 0: Client tax information -->
    <div [hidden]="inputTabsComponent.currentTabIndex !== 0">
      <client-tax-info (infoIconClicked)="onInfoIconClicked($event)"></client-tax-info>
    </div>

    <!-- Tab 1: Roth conversion details -->
    <div [hidden]="inputTabsComponent.currentTabIndex !== 1">
      <roth-conv-details (infoIconClicked)="onInfoIconClicked($event)"></roth-conv-details>
    </div>

    <!-- Right side charts/thesis -->
    <div slot="charts">
      <div class="rcc-v4__charts-panel">
        <!-- Scoped to the thesis text + chart, since both are derived from the same graph
             data — blocking only this area avoids a stale dollar figure sitting above a
             blank/updating chart while tax rates or the calculation are in flight. -->
        <div class="rcc-v4__chart-loading-region">
          @if (loading) {
          <div class="rcc-v4__chart-loading-overlay" role="status" aria-live="polite" aria-busy="true"></div>
          <ngx-axs-spinner
            class="rcc-v4__chart-loading-spinner"
            [axsActive]="true"
            [axsStrokeWidth]="4"
            [axsColor]="'red'">
          </ngx-axs-spinner>
          }
          <thesis
            [type]="inputsComplete ? currentThesisType : 'error'"
            [inputsComplete]="inputsComplete"
            [rothConversionTaxAmount]="rothConversionTaxAmount"
            [yearsUntilDist]="yearsUntilDist"
            [assetDifference]="assetDifference"
            [betrValue]="betrValue"
            [paymentMethod]="paymentMethod"
            (openTerminology)="openTerminologySheet()"
            (openBetr)="openSheetToBETR()"
            (openTaxAmount)="openSheetToCalculatorResults()">
          </thesis>

          <div class="rcc-v4__graph-wrapper">
            <graph-section-v4 #interactiveChart [currentThesisType]="currentThesisType"></graph-section-v4>
          </div>
        </div>

        <div *ngIf="currentThesisType !== 'error' && inputsComplete && !isIPad" class="rcc-v4__client-ready-wrapper">
          <div class="rcc-v4__client-ready-report elevation-50">
            <div class="rcc-v4__client-ready-report-text text-body-2 weight-bold">
              {{ clientReadyReportContent?.shareThisWithYourClient }}
            </div>
            <ngx-axs-button
              axsType="primary"
              axsTheme="black"
              axsSize="small"
              [axsShowIcon]="true"
              [axsIconType]="isExternal && lockedPrint ? 'lock' : 'download'"
              [axsText]="clientReadyReportContent?.prepareClientReadyReport || ''"
              (axsClick)="onPrepareReportClick()"
            ></ngx-axs-button>
            <a
              *ngIf="lockedPrint"
              class="rcc-v4__view-sample-link"
              [href]="sampleReportLink"
              target="_blank"
              rel="noopener noreferrer"
              (click)="adobeTagSampleReport()"
            >{{ clientReadyReportContent?.samplereport }}</a>
          </div>
        </div>
      </div>
    </div>
  </input-tabs>

  <div class="rcc-v4__mobile-edit-button">
    <p class="rcc-v4__mobile-edit-text">{{ mobileInputsSheetContent?.updateTheseResults }}</p>
    <button type="button"
            class="rcc-v4__mobile-edit-btn"
            (click)="openMobileInputsSheet()">
      {{ mobileInputsSheetContent?.editButton }}
    </button>
  </div>
</div>
<redesign-feedback-survey *ngIf="showFeedbackSurvey"></redesign-feedback-survey>
<client-ready-resources-v4 id="client-ready-resources"></client-ready-resources-v4>
<product-cards></product-cards>
<methodology id="methodology"></methodology>
<disclosures-v4></disclosures-v4>

<!-- Print-only static pages -->
<div class="printed-pdf">
  <!-- Page 1 - Cover Page -->
  <div class="printed-pdf__page">
    <img src="assets/images/Cover.png" alt="Roth Conversion Calculator Cover" />
  </div>

  <!-- Page 2 -->
  <div class="printed-pdf__page">
    <img src="assets/images/Pg1.png" alt="Roth Conversion Information Page 1" />
  </div>

  <!-- Page 3 -->
  <div class="printed-pdf__page">
    <img src="assets/images/Pg2.png" alt="Roth Conversion Information Page 2" />
  </div>

  <!-- Page 4 - Roth Conversion Report: Information used for this conversion scenario (dynamic) -->
  <div class="printed-pdf__page">
    <rcc-pdf-report-info [printClientName]="printClientName"></rcc-pdf-report-info>
  </div>

  <!-- Page 5 - Custom Results -->
  <div class="printed-pdf__page">
    <rcc-pdf-custom-results [printClientName]="printClientName" [chartImageDataUrl]="chartImageDataUrl"
      [currentThesisType]="currentThesisType" [rothConversionTaxAmount]="rothConversionTaxAmount"
      [yearsUntilDist]="yearsUntilDist" [assetDifference]="assetDifference" [betrValue]="betrValue"
      [paymentMethod]="paymentMethod" [hasProfitabilityPoint]="interactiveChart?.hasProfitabilityPoint" />
  </div>

  <!-- Page 6 - Methodology -->
  <div class="printed-pdf__page">
    <img src="assets/images/Methodology.png" alt="Methodology" />
  </div>

  <!-- Page 7 - Disclaimers -->
  <div class="printed-pdf__page">
    <img src="assets/images/Disclaimers.png" alt="Disclaimers and Legal Information" />
  </div>
</div>

<survey-side-button [formId]="environment.MEDALLIA_SURVEY_ID"></survey-side-button>

<terminology-sheet #terminologySheet></terminology-sheet>

<mobile-inputs-sheet
  #mobileInputsSheet
  (infoIconClicked)="onInfoIconClicked($event)"
  (updateClicked)="onMobileInputsUpdate()">
</mobile-inputs-sheet>

</div><!-- End of main content wrapper (exclude-from-print) -->

<client-ready-report-modal
  *ngIf="!isIPad"
  [(isOpen)]="isClientReadyReportModalOpen"
  (downloadWithCustomization)="onDownloadClientReadyReport($event)"
  (downloadWithoutCustomization)="onDownloadClientReadyReport()"
></client-ready-report-modal>

<advisor-certification-modal
  *ngIf="isExternal"
  instance-id="rcc-certification-modal"
  [showModal]="showCertModal"
  [appId]="environment.appPrefix"
  [heading]="certificationModalTitle"
  [hideSignInSection]="false"
  (advisor-certification-success)="onCertificationSuccess()"
  (advisor-certified-already)="onCertificationSuccess()"
  (advisor-certification-modal-closed)="onCertModalClosed()"
  (user-logged-on)="onCertificationSuccess()"
  (redirected-to-sign-in)="onRedirectedToSignIn()"
></advisor-certification-modal>
